import { styled, css, useTheme } from '@vebox/etui-system';
import { useCallback, createElement, Fragment, createRef } from 'react';
import { ellipsis } from './utilities/ellipsis.js';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from './_rollupPluginBabelHelpers-3b7bd988.js';
import { Li, Div } from '@vebox/etui-system-elements';
import Icon from './Icon/Icon.js';
import { icons } from './Icon/icons.js';
import { darken } from '@vebox/etui-colors';
import Tooltip from './Tooltip/Tooltip.js';
import Button from './Button/Button.js';
import Checkbox from './Checkbox/Checkbox.js';
import { g as getNestedTreeIds, b as getCheckedNestedTreeIds } from './index-5100f354.js';
import { focusNextSiblingOf, focusPreviousSiblingOf } from './TreeView/utils/focus.js';

const _excluded = ["theme"];
const TreeViewItemLi = styled(Li).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2(_objectSpread2({}, theme.typography.button), {}, {
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.primary.main,
    m: 0,
    listStyle: 'none',
    cursor: 'pointer',
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  }, props);
})``;

const _excluded$1 = ["isRowSelected", "showCheckBox", "theme"];
const TreeViewItemContentDiv = styled(Div).attrs(_ref => {
  let {
      isRowSelected,
      showCheckBox,
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$1);
  return _objectSpread2({
    display: 'flex',
    alignItems: 'center',
    p: `${theme.spacing.xs}px`,
    minHeight: '38px',
    color: theme.palette.primary.main,
    fontWeight: isRowSelected && !showCheckBox ? 700 : 400,
    textDecoration: 'none',
    outline: 0
  }, props);
})`
  :hover {
    background-color: ${({
  theme
}) => theme.palette.action.hover};
  }
`;

const _excluded$2 = ["level", "theme"];
const TreeViewItemToggleDiv = styled(Div).attrs(_ref => {
  let {
      level,
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$2);
  return _objectSpread2({
    color: theme.palette.text.secondary,
    borderRadius: theme.shape.borderRadius,
    p: `${theme.spacing.xxs}px`,
    lineHeight: 0,
    ml: level === 0 ? 0 : `${level * theme.spacing.lg}px`
  }, props);
})`
  transition: all 0.2s;
  ${({
  hasNestedItems
}) => !hasNestedItems && css`
      visibility: hidden;
    `}

  &:focus,
  &:hover {
    outline: 0;
    color: ${({
  theme
}) => theme.palette.text.primary};
    background-color: ${({
  isRowSelected,
  theme
}) => isRowSelected ? darken(theme.palette.action.hover, 0.075) : theme.palette.action.hover};
  }
`;

const _excluded$3 = ["theme"];
const TreeViewSubItemDiv = styled(Div).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$3);
  return _objectSpread2({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSizeBase,
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.grey[100]
  }, props);
})``;

const _excluded$4 = ["theme"];
const TreeViewItemDiv = styled(Div).attrs(_ref => {
  let props = _objectWithoutProperties(_ref, _excluded$4);
  return _objectSpread2({
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }, props);
})``;

const CHECKBOX_PREFIX = 'checkbox-';
const keyCodes = {
  DOWN: '40',
  END: '35',
  ENTER: '13',
  HOME: '36',
  LEFT: '37',
  RIGHT: '39',
  SPACE: '32',
  UP: '38'
};
const TreeViewItem = ({
  as = 'div',
  item,
  onToggle,
  isOpen,
  openSections,
  checkedItems,
  showCheckBox,
  onCheckboxClick,
  onDragStart,
  onDragEnd,
  onDrop,
  allowDragAndDrop,
  rowTemplate,
  isRowSelected,
  onItemClick,
  styles = {},
  level = 0,
  disableLabelSelection = false
}) => {
  var _item$items;
  const treeViewItemContentRef = /*#__PURE__*/createRef();
  const theme = useTheme();
  const nestedItems = item.items || null;
  const hasNestedItems = !!(nestedItems !== null && nestedItems !== void 0 && nestedItems.length);
  const toggleDivId = 'toggle';
  const chevronId = 'chevron-down';
  const isChevron = targetId => toggleDivId === targetId || chevronId === targetId;
  const handleToggleClick = useCallback(customizedRowItem => {
    const isOpenNew = customizedRowItem ? !openSections.includes(customizedRowItem.id) : !isOpen;
    onToggle(customizedRowItem || item, isOpenNew);
  }, [isOpen, item, onToggle, openSections]);
  const handleCheckBoxClick = customizedRowItem => {
    const isChecked = checkedItems.includes(customizedRowItem.id || item.id);
    onCheckboxClick(customizedRowItem || item, !isChecked);
  };
  const handleItemClick = (e, customizedRowItem) => {
    e.stopPropagation();
    const targetId = e.target.id;
    const targetItem = customizedRowItem || item;
    if ((treeViewItemContentRef === null || treeViewItemContentRef === void 0 ? void 0 : treeViewItemContentRef.current) !== e.target && targetId !== `${targetItem.id}-label` && !targetItem.name && !targetItem.allowRowSelection && !isChevron(targetId)) {
      return;
    }
    if (isChevron(targetId)) {
      handleToggleClick(customizedRowItem);
      return;
    }
    if (disableLabelSelection && hasNestedItems) {
      handleToggleClick(customizedRowItem);
      onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick(targetItem);
      return;
    }
    if (!isRowSelected(targetItem) && isOpen && !showCheckBox) {
      onItemClick(targetItem);
      return;
    }
    onItemClick(targetItem);
  };
  const handleCheckBoxChange = () => handleCheckBoxClick();
  const handleDragStart = e => onDragStart(e, item);
  const handleDragEnd = e => onDragEnd(e, item);
  const handleDrop = e => onDrop(e, item);
  const nestedItemsIds = getNestedTreeIds(item).filter(id => id !== item.id);
  const checkedNestedIds = getCheckedNestedTreeIds(nestedItemsIds, checkedItems);
  const handleKeyPress = (e, customizedRowItem) => {
    const keyCode = e.which.toString();
    const targetItem = customizedRowItem || item;
    if ((keyCode === keyCodes.ENTER || keyCode === keyCodes.SPACE) && !targetItem.isTextInput) {
      e.preventDefault();
      const targetId = e.target.id;
      const isCheckbox = targetId === `${CHECKBOX_PREFIX}${targetItem.id}`;
      if (showCheckBox && !isChevron(targetId) && isCheckbox) {
        handleCheckBoxClick(customizedRowItem);
        return;
      }
      if (!isChevron(targetId)) {
        handleItemClick(e, customizedRowItem);
        return;
      }
      handleToggleClick(customizedRowItem);
    }
    if (e.target instanceof HTMLElement) {
      switch (keyCode) {
        case keyCodes.UP:
          {
            e.preventDefault();
            focusPreviousSiblingOf(e.target);
            break;
          }
        case keyCodes.DOWN:
          {
            e.preventDefault();
            focusNextSiblingOf(e.target);
            break;
          }
        case keyCodes.LEFT:
          {
            if (!targetItem.isTextInput) {
              e.preventDefault();
              if (openSections.includes(targetItem.id)) {
                handleToggleClick(customizedRowItem);
              }
            }
            break;
          }
        case keyCodes.RIGHT:
          {
            if (!targetItem.isTextInput) {
              e.preventDefault();
              if (!openSections.includes(targetItem.id)) {
                handleToggleClick(customizedRowItem);
              }
            }
            break;
          }
      }
    }
  };
  const hasCheckedNestedItems = checkedNestedIds.length > 0;
  const isCheckboxIndeterminate = hasCheckedNestedItems && checkedNestedIds.length < nestedItemsIds.length;
  const allNestedItemsChecked = hasCheckedNestedItems && nestedItemsIds.length === checkedNestedIds.length;
  const getCustomRowOptions = () => ({
    level,
    item,
    hasNestedItems,
    showCheckBox,
    openSections,
    checkedItems,
    isOpen,
    isRowSelected,
    getToggleProps: () => ({
      onClick: handleItemClick,
      onKeyDown: handleKeyPress,
      id: toggleDivId,
      role: 'button',
      tabIndex: -1
    }),
    getItemRowProps: () => ({
      onClick: handleItemClick,
      onKeyDown: handleKeyPress,
      dataComponent: 'TreeViewItem',
      role: 'button',
      tabIndex: 0
    }),
    getCheckboxProps: () => ({
      id: `${CHECKBOX_PREFIX}${item.id}`,
      name: item.name,
      onChange: handleCheckBoxClick,
      checked: hasNestedItems ? allNestedItemsChecked : checkedItems.includes(item.id),
      indeterminate: hasNestedItems && isCheckboxIndeterminate
    })
  });
  const rowTemplateFn = rowTemplate ? rowTemplate({
    item,
    options: getCustomRowOptions()
  }) : null;
  return /*#__PURE__*/createElement(TreeViewItemLi, _extends({
    className: "tree-item-row",
    draggable: allowDragAndDrop,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDrop: handleDrop
  }, styles.root), rowTemplateFn || /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(TreeViewItemDiv, styles.nestedColumn, /*#__PURE__*/createElement(TreeViewItemDiv, {
    mr: theme.spacing.xxs
  }, /*#__PURE__*/createElement(TreeViewItemContentDiv, _extends({
    ref: treeViewItemContentRef,
    as: as,
    "data-component": "TreeViewItem",
    role: "button",
    tabIndex: 0,
    onKeyDown: handleKeyPress,
    isRowSelected: isRowSelected(item),
    onClick: handleItemClick,
    showCheckBox: showCheckBox
  }, item), /*#__PURE__*/createElement(TreeViewItemToggleDiv, {
    id: toggleDivId,
    role: "button",
    tabIndex: -1,
    lineHeight: 0,
    onKeyDown: handleKeyPress,
    onClick: handleItemClick,
    hasNestedItems: hasNestedItems,
    isRowSelected: isRowSelected(item),
    level: level
  }, /*#__PURE__*/createElement(Icon, {
    glyph: icons.CHEVRON_DOWN,
    rotate: isOpen ? 0 : -90,
    size: 16
  })), showCheckBox && /*#__PURE__*/createElement(Div, {
    ml: `${theme.spacing.xxs}px`
  }, /*#__PURE__*/createElement(Checkbox, {
    id: `${CHECKBOX_PREFIX}${item.id}`,
    name: item.name,
    onChange: handleCheckBoxChange,
    checked: hasNestedItems ? allNestedItemsChecked : checkedItems.includes(item.id),
    indeterminate: hasNestedItems && isCheckboxIndeterminate
  })), /*#__PURE__*/createElement(Tooltip, {
    message: item === null || item === void 0 ? void 0 : item.toolTipMessage,
    placement: "right",
    disableToolTip: !((item === null || item === void 0 ? void 0 : (_item$items = item.items) === null || _item$items === void 0 ? void 0 : _item$items.length) === 0 && item !== null && item !== void 0 && item.toolTipMessage)
  }, /*#__PURE__*/createElement(Div, _extends({
    ml: `${item !== null && item !== void 0 && item.glyph ? 0 : theme.spacing.xs}px`
  }, ellipsis()), item !== null && item !== void 0 && item.glyph ? /*#__PURE__*/createElement(Button, {
    onClick: handleItemClick,
    testId: "no tree",
    iconProps: {
      glyph: icons[item.glyph]
    },
    styles: {
      root: {
        m: 0,
        p: 0,
        color: theme.palette.primary.main
      },
      text: {
        color: theme.palette.primary.main,
        fontWeight: theme.typography.fontSizeBase,
        fontSize: theme.typography.fontSizeBase
      }
    }
  }, item !== null && item !== void 0 && item.rowTemplate ? item.rowTemplate({
    item,
    options: getCustomRowOptions()
  }) : item.name) : /*#__PURE__*/createElement(Div, {
    position: "relative",
    display: "inline-block"
  }, /*#__PURE__*/createElement(Div, {
    id: `${item.id}-label`
  }, item !== null && item !== void 0 && item.rowTemplate ? item.rowTemplate({
    item,
    options: getCustomRowOptions()
  }) : item.name), item.subItem && /*#__PURE__*/createElement(TreeViewSubItemDiv, null, item.subItem)))))), isOpen && hasNestedItems && item.items.map((childNode, index) => /*#__PURE__*/createElement(TreeViewItem, {
    item: childNode,
    key: childNode.id + childNode.name + index,
    rowTemplate: rowTemplate,
    as: as,
    allowDragAndDrop: allowDragAndDrop,
    showCheckBox: showCheckBox,
    openSections: openSections,
    checkedItems: checkedItems,
    isOpen: openSections.includes(childNode.id),
    onToggle: onToggle,
    onCheckboxClick: onCheckboxClick,
    onDragStart: onDragStart,
    onDragEnd: onDragEnd,
    onDrop: onDrop,
    isRowSelected: isRowSelected,
    onItemClick: onItemClick,
    styles: styles,
    level: level + 1,
    disableLabelSelection: disableLabelSelection
  }))), !hasNestedItems && item.content && /*#__PURE__*/createElement(Fragment, null, item.content)));
};

export { TreeViewItemLi as T, TreeViewItemContentDiv as a, TreeViewItemToggleDiv as b, TreeViewSubItemDiv as c, TreeViewItemDiv as d, TreeViewItem as e };
